import { cn } from "@/modern/utils";
import type { BucketStatus, ProblemHub, SequenceStatus } from "../types";

// ---------------------------------------------------------------------------
// Badge style constants & helpers (display only — statuses come from the API)
// ---------------------------------------------------------------------------

export const COVERAGE_BADGE_BASE = "!text-2xs !py-0.5 !border-transparent";

export const BUCKET_STATUS_TOOLTIPS: Record<BucketStatus, string> = {
  empty: "Fewer than 40% of content roles have any assets. This hub needs significant work before output generation is possible.",
  partial: "40–79% of content roles have at least one asset. Some roles are still missing — fill the gaps to reach output readiness.",
  ready: "80–99% of content roles have at least one asset. Almost fully covered — just a few roles left to fill.",
  strong: "All content roles have at least one asset. This hub is fully covered and output-ready.",
};

export const SEQUENCE_STATUS_TOOLTIPS: Record<SequenceStatus, string> = {
  sequence_ready: "Before, during, and after assets are all present. Full sequence outputs are possible.",
  before_after_available: "Before and after assets exist but during is missing. Add in-progress images to unlock full sequence capability.",
  missing_before: "After assets exist but before assets are missing. Add pre-work images to complete the sequence.",
  missing_after: "Before assets exist but after assets are missing. Add post-repair images to complete the sequence.",
  no_sequence: "No temporal coverage yet. Promote assets with before, during, or after classifications.",
};

export const BUCKET_STATUS_LABELS: Record<BucketStatus, string> = {
  empty: "Empty",
  partial: "Partial",
  ready: "Ready",
  strong: "Strong",
};

export const PROBLEM_HUB_STATUS_BADGE_CLASSES: Record<BucketStatus, string> = {
  empty: "!bg-muted/50 !text-muted-foreground",
  partial: "!bg-amber-500/15 !text-amber-500",
  ready: "!bg-primary/15 !text-primary",
  strong: "!bg-success/15 !text-success",
};

export const SEQUENCE_STATUS_BADGE_CLASSES: Record<SequenceStatus, string> = {
  sequence_ready: "!bg-success/15 !text-success",
  before_after_available: "!bg-primary/15 !text-primary",
  missing_before: "!bg-amber-500/15 !text-amber-500",
  missing_after: "!bg-amber-500/15 !text-amber-500",
  no_sequence: "!bg-muted/50 !text-muted-foreground",
};

export const SEQUENCE_STATUS_LABELS: Record<SequenceStatus, string> = {
  sequence_ready: "Sequence ready",
  before_after_available: "Before / After",
  missing_before: "Missing before",
  missing_after: "Missing after",
  no_sequence: "No sequence",
};

export const COVERAGE_KPI_CONTEXT_BADGE_CLASSES = {
  landscape_ready: "!bg-success/15 !text-success",
  sequence_capable: "!bg-primary/15 !text-primary",
  blind_spot_attention: "!bg-destructive/15 !text-destructive",
  blind_spot_clear: "!bg-success/15 !text-success",
} as const;

export function problemHubStatusBadgeClass(status: BucketStatus): string {
  const fragment = PROBLEM_HUB_STATUS_BADGE_CLASSES[status];
  return cn(COVERAGE_BADGE_BASE, fragment ?? PROBLEM_HUB_STATUS_BADGE_CLASSES.empty);
}

export function sequenceBadgeClassFromStatus(status: SequenceStatus): string {
  const fragment = SEQUENCE_STATUS_BADGE_CLASSES[status];
  return cn(COVERAGE_BADGE_BASE, fragment ?? SEQUENCE_STATUS_BADGE_CLASSES.no_sequence);
}

export const TEMPORAL_PHASES = ["before", "during", "after"] as const;
export type TemporalPhase = (typeof TEMPORAL_PHASES)[number];

/** Hub row % — always from API `progress_percent` (never recomputed locally). */
export function getHubProgress(hub: ProblemHub): number {
  return Math.round(hub.progressPercent ?? 0);
}

/** Color from API overall_status + sequence_status (display only). */
export function getHubProgressBarClass(hub: ProblemHub): string {
  if (hub.overallStatus === "strong" && hub.sequenceStatus === "sequence_ready") return "bg-success";
  if (hub.overallStatus === "strong" || hub.overallStatus === "ready") return "bg-primary";
  if (hub.overallStatus === "partial") return "bg-amber-500";
  return "bg-muted-foreground/50";
}

export function getHubProgressTextClass(hub: ProblemHub): string {
  if (hub.overallStatus === "strong" && hub.sequenceStatus === "sequence_ready") return "text-success";
  if (hub.overallStatus === "strong" || hub.overallStatus === "ready") return "text-primary";
  if (hub.overallStatus === "partial") return "text-amber-500";
  return "text-foreground/50";
}

export function kpiContextBadgeClass(
  key: keyof typeof COVERAGE_KPI_CONTEXT_BADGE_CLASSES,
  layoutClass?: string
): string {
  return cn(COVERAGE_BADGE_BASE, COVERAGE_KPI_CONTEXT_BADGE_CLASSES[key], layoutClass);
}
